import type { DateValue } from "@ark-ui/react/date-picker";
import { DatePicker, parseDate } from "@ark-ui/react/date-picker";
import { Box, Button, Field, HStack, IconButton, Input, Portal, Stack, Text } from "@chakra-ui/react";
import moment from "moment";
import { useState, useEffect, useRef } from "react";
import { CiCalendar } from "react-icons/ci";
import { MdOutlineChevronLeft, MdOutlineChevronRight } from "react-icons/md";
import "./appDatePicker.css";

import { DATE_FORMAT } from "../../config/constants";

interface AppDatePickerProps extends DatePicker.RootProps {
  date: Date | null;
  minDate?: Date;
  maxDate?: Date;
  setDate: (date: Date | null) => void;
  size?: "xs" | "sm" | "md" | "lg";
  title?: string;
  placeholder?: string;
  zIndex?: number;
}

function format(dateValue: DateValue | null): string {
  if (!dateValue) {
    return "";
  }
  let jsDate: Date;
  if (typeof (dateValue as { toDate?: () => Date }).toDate === "function") {
    jsDate = (dateValue as { toDate: () => Date }).toDate();
  } else {
    jsDate = dateValue as unknown as Date;
  }
  return moment(jsDate).format(DATE_FORMAT);
}

function parse(value: string): DateValue | undefined {
  const m = moment(value, DATE_FORMAT, true);
  if (m.isValid()) {
    return parseDate(m.toDate());
  }
  return undefined;
}

export const AppDatePicker = (props: AppDatePickerProps) => {
  const { date, minDate, maxDate, setDate, size, title, placeholder, ...restProps } = props;
  const [value, setValue] = useState<DateValue[]>([]);
  const [input, setInput] = useState(date ? moment(date).format(DATE_FORMAT) : "");
  const prevDateRef = useRef<Date | null>(date);

  useEffect(() => {
    if (
      (date && !prevDateRef.current) ||
      (!date && prevDateRef.current) ||
      (date && prevDateRef.current && +date !== +prevDateRef.current)
    ) {
      setInput(date ? moment(date).format(DATE_FORMAT) : "");
      setValue(date ? [parseDate(moment(date).format(DATE_FORMAT))] : []);
      prevDateRef.current = date;
    }
  }, [date]);

  function onValueChange(details: DatePicker.ValueChangeDetails) {
    setValue(details.value);
    if (details.valueAsString.length > 0) {
      const newDate = details.valueAsString[0];
      setDate(new Date(newDate));
      setInput(moment(newDate).format(DATE_FORMAT));
    }
  }

  function handleInputChange(val: string) {
    setInput(val);
    const m = moment(val, DATE_FORMAT, true);
    if (val.length === DATE_FORMAT.length && m.isValid()) {
      setDate(m.toDate());
      setValue([parseDate(val)]);
    }
  }

  const min =
    minDate instanceof Date && !isNaN(minDate.getTime()) ? parseDate(moment(minDate).format(DATE_FORMAT)) : undefined;
  const max =
    maxDate instanceof Date && !isNaN(maxDate.getTime()) ? parseDate(moment(maxDate).format(DATE_FORMAT)) : undefined;

  return (
    <Stack zIndex={props.zIndex ?? 1}>
      <Field.Root m={title ? undefined : 0} p={title ? undefined : 0}>
        {title && (
          <Field.Label>
            <Text as="b" fontSize={"xs"}>
              {title}
            </Text>
          </Field.Label>
        )}
        <DatePicker.Root
          positioning={{ placement: "bottom-start", sameWidth: true, gutter: 8, strategy: "fixed" }}
          startOfWeek={1}
          numOfMonths={1}
          onValueChange={onValueChange}
          value={value}
          {...restProps}
          min={min}
          max={max}
          format={format}
          parse={parse}
        >
          <DatePicker.Control>
            <HStack gap={2}>
              <Input
                size={size}
                value={input}
                placeholder={placeholder ?? "pick a date"}
                autoComplete="off" // Prevent browser autofill
                onChange={e => {
                  const normalized = e.target.value.replace(/[^0-9./-]/g, "");
                  handleInputChange(normalized);
                }}
              />
              <DatePicker.Trigger asChild>
                <IconButton w="5%" variant="ghost" aria-label="Open date picker">
                  <CiCalendar />
                </IconButton>
              </DatePicker.Trigger>
            </HStack>
          </DatePicker.Control>
          <Portal>
            <DatePicker.Positioner style={{ zIndex: 2147483647 }}>
              <DatePicker.Content>
                <DatePicker.View view="day">
                  <DatePicker.Context>
                    {api => (
                      <>
                        <DatePicker.ViewControl>
                          <DatePicker.PrevTrigger asChild>
                            <IconButton variant="ghost" size="sm">
                              <MdOutlineChevronLeft />
                            </IconButton>
                          </DatePicker.PrevTrigger>
                          <DatePicker.ViewTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <DatePicker.RangeText />
                            </Button>
                          </DatePicker.ViewTrigger>
                          <DatePicker.NextTrigger asChild>
                            <IconButton variant="ghost" size="sm">
                              <MdOutlineChevronRight />
                            </IconButton>
                          </DatePicker.NextTrigger>
                        </DatePicker.ViewControl>
                        <DatePicker.Table>
                          <DatePicker.TableHead>
                            <DatePicker.TableRow>
                              {api.weekDays.map((weekDay, id) => (
                                <DatePicker.TableHeader key={id}>{weekDay.narrow}</DatePicker.TableHeader>
                              ))}
                            </DatePicker.TableRow>
                          </DatePicker.TableHead>
                          <DatePicker.TableBody>
                            {api.weeks.map((week, id) => (
                              <DatePicker.TableRow key={id}>
                                {week.map((day, id) => (
                                  <DatePicker.TableCell key={id} value={day}>
                                    <DatePicker.TableCellTrigger asChild>
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        width="2em"
                                        height="2em"
                                        borderRadius="md"
                                        _hover={{ bg: "brand.subtle" }}
                                      >
                                        {day.day}
                                      </Box>
                                    </DatePicker.TableCellTrigger>
                                  </DatePicker.TableCell>
                                ))}
                              </DatePicker.TableRow>
                            ))}
                          </DatePicker.TableBody>
                        </DatePicker.Table>
                      </>
                    )}
                  </DatePicker.Context>
                </DatePicker.View>
                <DatePicker.View view="month">
                  <DatePicker.Context>
                    {api => (
                      <>
                        <DatePicker.ViewControl>
                          <DatePicker.PrevTrigger asChild>
                            <IconButton variant="ghost" size="sm">
                              <MdOutlineChevronLeft />
                            </IconButton>
                          </DatePicker.PrevTrigger>
                          <DatePicker.ViewTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <DatePicker.RangeText />
                            </Button>
                          </DatePicker.ViewTrigger>
                          <DatePicker.NextTrigger asChild>
                            <IconButton variant="ghost" size="sm">
                              <MdOutlineChevronRight />
                            </IconButton>
                          </DatePicker.NextTrigger>
                        </DatePicker.ViewControl>
                        <DatePicker.Table>
                          <DatePicker.TableBody>
                            {api.getMonthsGrid({ columns: 4, format: "short" }).map((months, id) => (
                              <DatePicker.TableRow key={id}>
                                {months.map((month, id) => (
                                  <DatePicker.TableCell key={id} value={month.value}>
                                    <DatePicker.TableCellTrigger asChild>
                                      <Button variant="ghost">{month.label}</Button>
                                    </DatePicker.TableCellTrigger>
                                  </DatePicker.TableCell>
                                ))}
                              </DatePicker.TableRow>
                            ))}
                          </DatePicker.TableBody>
                        </DatePicker.Table>
                      </>
                    )}
                  </DatePicker.Context>
                </DatePicker.View>
                <DatePicker.View view="year">
                  <DatePicker.Context>
                    {api => (
                      <>
                        <DatePicker.ViewControl>
                          <DatePicker.PrevTrigger asChild>
                            <IconButton variant="ghost" size="sm">
                              <MdOutlineChevronLeft />
                            </IconButton>
                          </DatePicker.PrevTrigger>
                          <DatePicker.ViewTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <DatePicker.RangeText />
                            </Button>
                          </DatePicker.ViewTrigger>
                          <DatePicker.NextTrigger asChild>
                            <IconButton variant="ghost" size="sm">
                              <MdOutlineChevronRight />
                            </IconButton>
                          </DatePicker.NextTrigger>
                        </DatePicker.ViewControl>
                        <DatePicker.Table>
                          <DatePicker.TableBody>
                            {api.getYearsGrid({ columns: 4 }).map((years, id) => (
                              <DatePicker.TableRow key={id}>
                                {years.map((year, id) => (
                                  <DatePicker.TableCell key={id} value={year.value}>
                                    <DatePicker.TableCellTrigger asChild>
                                      <Button variant="ghost">{year.label}</Button>
                                    </DatePicker.TableCellTrigger>
                                  </DatePicker.TableCell>
                                ))}
                              </DatePicker.TableRow>
                            ))}
                          </DatePicker.TableBody>
                        </DatePicker.Table>
                      </>
                    )}
                  </DatePicker.Context>
                </DatePicker.View>
              </DatePicker.Content>
            </DatePicker.Positioner>
          </Portal>
        </DatePicker.Root>
      </Field.Root>
    </Stack>
  );
};
